import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Check, 
  AlertTriangle, 
  XCircle,
  Share2,
  Download,
  Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { resultsApi } from '@/services/api';
import { usePlatforms } from '@/hooks/usePlatforms';
import { exportResultsToPDF } from '@/utils/pdfExport';
import { cn } from '@/lib/utils';

const SuitabilityBadge = ({ suitability }) => {
  const config = {
    suitable: { label: 'Suitable', className: 'bg-success text-success-foreground', icon: Check },
    caution: { label: 'Partially suitable', className: 'bg-warning text-warning-foreground', icon: AlertTriangle },
    'not-recommended': { label: 'Not recommended', className: 'bg-destructive text-destructive-foreground', icon: XCircle },
  };
  const { label, className, icon: Icon } = config[suitability] || config.caution;
  
  return (
    <Badge className={cn('gap-1', className)}>
      <Icon className="w-3 h-3" />
      {label}
    </Badge>
  );
};

export default function SharedResultsPage() {
  const { shareCode } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sharedData, setSharedData] = useState(null);
  const { getPlatformById } = usePlatforms();

  useEffect(() => {
    const loadSharedResults = async () => {
      try {
        const data = await resultsApi.get(shareCode);
        setSharedData(data);
      } catch (err) {
        setError('This shared link is invalid or has expired.');
      } finally {
        setLoading(false);
      }
    };

    if (shareCode) {
      loadSharedResults();
    }
  }, [shareCode]);

  const handleExportPDF = () => {
    if (sharedData?.platforms) {
      const enrichedPlatforms = sharedData.platforms.map(p => {
        const fullPlatform = getPlatformById(p.id);
        return {
          ...fullPlatform,
          ...p,
          reasons: {
            suitable: p.reasons_suitable || [],
            concerns: p.reasons_concerns || [],
          }
        };
      });
      exportResultsToPDF(enrichedPlatforms, sharedData.answers || {});
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      // Could add a toast here
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Loading shared results...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center max-w-md mx-auto px-4">
            <XCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
            <h1 className="text-2xl font-semibold text-foreground mb-2">Link not found</h1>
            <p className="text-muted-foreground mb-6">{error}</p>
            <Button onClick={() => navigate('/')}>Start your own comparison</Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const platforms = sharedData?.platforms || [];
  const createdAt = sharedData?.created_at ? new Date(sharedData.created_at).toLocaleDateString('en-GB') : null;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      
      <main className="flex-1 w-full">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="-ml-2 gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Start fresh
            </Button>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopyLink}
                className="gap-2"
              >
                <Share2 className="w-4 h-4" />
                Copy link
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportPDF}
                className="gap-2"
              >
                <Download className="w-4 h-4" />
                Export PDF
              </Button>
            </div>
          </div>

          <div className="animate-fade-in">
            {/* Title */}
            <div className="mb-8">
              <Badge variant="outline" className="mb-3">Shared Results</Badge>
              <h1 className="text-3xl font-semibold text-foreground mb-2">
                AI Platform Recommendations
              </h1>
              <p className="text-muted-foreground">
                {createdAt && `Saved on ${createdAt} • `}
                {platforms.length} platforms compared
              </p>
            </div>

            {/* Results */}
            <div className="space-y-4">
              {platforms.map((platform, index) => {
                const fullPlatform = getPlatformById(platform.id);
                
                return (
                  <Card key={platform.id} className="overflow-hidden">
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-lg flex items-center gap-2">
                            <span className="text-muted-foreground font-normal">#{index + 1}</span>
                            {platform.name}
                          </CardTitle>
                          <p className="text-sm text-muted-foreground">{platform.provider}</p>
                        </div>
                        <SuitabilityBadge suitability={platform.suitability} />
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground mb-4">
                        {platform.summary}
                      </p>
                      
                      {/* Match percentage */}
                      <div className="mb-4">
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-muted-foreground">Match score</span>
                          <span className="font-medium">{Math.round(platform.percentage)}%</span>
                        </div>
                        <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                          <div 
                            className={cn(
                              "h-full rounded-full transition-all",
                              platform.suitability === 'suitable' ? 'bg-success' :
                              platform.suitability === 'caution' ? 'bg-warning' : 'bg-destructive'
                            )}
                            style={{ width: `${platform.percentage}%` }}
                          />
                        </div>
                      </div>
                      
                      {/* Reasons */}
                      <div className="grid md:grid-cols-2 gap-4">
                        {platform.reasons_suitable?.length > 0 && (
                          <div>
                            <p className="text-xs font-medium text-success mb-2">Why it works</p>
                            <ul className="space-y-1">
                              {platform.reasons_suitable.map((reason, i) => (
                                <li key={i} className="text-xs text-muted-foreground flex items-start gap-1">
                                  <Check className="w-3 h-3 text-success mt-0.5 flex-shrink-0" />
                                  {reason}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {platform.reasons_concerns?.length > 0 && (
                          <div>
                            <p className="text-xs font-medium text-warning mb-2">Considerations</p>
                            <ul className="space-y-1">
                              {platform.reasons_concerns.map((reason, i) => (
                                <li key={i} className="text-xs text-muted-foreground flex items-start gap-1">
                                  <AlertTriangle className="w-3 h-3 text-warning mt-0.5 flex-shrink-0" />
                                  {reason}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                      
                      {fullPlatform && (
                        <div className="mt-4 pt-4 border-t">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate(`/platform/${platform.id}`)}
                          >
                            View full details
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* CTA */}
            <Card className="mt-8 bg-primary/5 border-primary/20">
              <CardContent className="py-6 text-center">
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  Want personalised recommendations?
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Take our quick questionnaire to get AI platform recommendations tailored to your specific needs.
                </p>
                <Button onClick={() => navigate('/')}>
                  Start your comparison
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
